import { useTranslation } from 'react-i18next';
import { INCIDENT_LIST_STATUS_TABS, type IncidentListStatusTab } from '../../models/Filters';

export type IncidentStatusCounts = Record<string, number>;

interface IncidentStatusToolbarProps {
  activeStatus: IncidentListStatusTab;
  onStatusChange: (status: IncidentListStatusTab) => void;
  statusCounts?: IncidentStatusCounts;
  isLoadingCounts?: boolean;
}

const formatCount = (count: number | undefined): string => {
  if (count === undefined) return '';
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return String(count);
};

/**
 * Barra de estados alineada visualmente con Pedidos (`StatusToolbar`): fondo card, scroll horizontal y badge de total.
 */
export const IncidentStatusToolbar = ({
  activeStatus,
  onStatusChange,
  statusCounts = {},
  isLoadingCounts = false,
}: IncidentStatusToolbarProps) => {
  const { t } = useTranslation('incidentsModule');

  return (
    <div
      className="flex bg-card-bg rounded-lg text-sm p-1 mb-4 overflow-x-auto scrollbar-hide light:border-card-border light:border"
      style={{ WebkitOverflowScrolling: 'touch' }}
      role="tablist"
      aria-label={t('tabs.status_filter', 'Filtrar por estado')}
    >
      {INCIDENT_LIST_STATUS_TABS.map((status) => {
        const countText = formatCount(statusCounts[status]);
        const isActive = activeStatus === status;
        const label = status === 'all' ? t('tabs.all', 'Todos') : t(`table.statuses.${status}`);

        return (
          <button
            key={status}
            type="button"
            onClick={() => onStatusChange(status)}
            data-testid={`incidents-status-tab-${status}`}
            className={`px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap flex items-center gap-1.5 ${
              isActive ? 'bg-hover-press' : ''
            }`}
          >
            <span>{label}</span>
            {countText !== '' && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                  isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                } ${isLoadingCounts ? 'animate-pulse' : ''}`}
              >
                {countText}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
